import { ReportFilter } from "fleet/view/report/report-filter";
import UptimeChart from "fleet/view/report/uptime-chart";
import orderBy from "lodash/orderBy";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { Link } from "react-router-dom";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { Table, Column } from "ui/table";
import { getErrorMessage } from "util/response";
import { utcToLocalFormat } from "util/time";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class UptimeReportPage extends React.PureComponent {
  static get propTypes() {
    return {
      devices: PropTypes.object.isRequired,
      end: PropTypes.string,
      fetchUptimeReport: PropTypes.func.isRequired,
      history: PropTypes.object.isRequired,
      networks: PropTypes.object.isRequired,
      network: PropTypes.object,
      networkOptions: PropTypes.array.isRequired,
      report: PropTypes.object.isRequired,
      start: PropTypes.string,
      storesLoaded: PropTypes.bool.isRequired,
      unloadAdDeliveryReport: PropTypes.func.isRequired,
    };
  }

  constructor() {
    super();

    this.state = {
      errorMessage: "",
      submitted: false,
    };
    this.uptimeExportHeader = [["Date", "Uptime"]];

    this.tableOpts = {
      sortName: "date",
      sortOrder: "desc",
    };
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    this._fetchReport(this.props);
  }

  componentDidUpdate(prevProps) {
    const { network, start, end } = prevProps;
    if (
      network !== this.props.network ||
      start !== this.props.start ||
      end !== this.props.end
    ) {
      this._fetchReport(this.props);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.props.unloadAdDeliveryReport();
  }

  _doFetchReport(props) {
    const { fetchUptimeReport, network, start, end } = props;
    const serviceArgs = {
      start,
      end,
      networkId: network.id,
    };

    return fetchUptimeReport(serviceArgs)
      .catch((e) => {
        if (this._isMounted) {
          this.setState({ errorMessage: getErrorMessage(e) });
        }
      })
      .finally(() => {
        if (this._isMounted) {
          this.setState({ submitted: false });
        }
      });
  }

  _fetchReport(props) {
    const { network, start, end } = props;
    if (network && start && end) {
      const doFetchReport = this._doFetchReport.bind(this, props);
      this.setState({ submitted: true }, doFetchReport);
    }
  }

  _onReportRun(networkId, start, end) {
    const url = `/reports/uptime/${networkId}/${start}/${end}`;
    this.props.history.push(url);
  }

  _exportFileName(prefix) {
    const { start, end, network } = this.props;
    const name = network ? network.name : "";
    return `${prefix}_${name}_${start}_${end}.csv`;
  }

  _formatDate(date) {
    const { network, start, end } = this.props;
    if (!network) {
      return utcToLocalFormat(date, "MM/DD/YYYY");
    }

    const url =
      `/reports/uptime/${network.id}/` +
      `${start}/${end}/devices/` +
      `${date.format("YYYY-MM-DD")}`;

    return <Link to={url}>{utcToLocalFormat(date, "MM/DD/YYYY")}</Link>;
  }

  _formatUptime(uptime) {
    return `${uptime.toFixed(2)}%`;
  }

  _exportUptimeData(data) {
    const { sortName, sortOrder } = this.tableOpts;
    const uptimeData = orderBy(data, sortName, sortOrder).map((row) => {
      return [row.date.format(), row.uptime];
    });

    return [...this.uptimeExportHeader, ...uptimeData];
  }

  _renderNetworkUptimeReport() {
    const { report } = this.props;
    if (Object.entries(report).length === 0) {
      return <p>No data to display.</p>;
    }

    const data = Object.keys(report).map((date) => {
      return {
        date: moment(date),
        uptime: report[date],
      };
    });
    const _export = this._exportUptimeData.bind(this, data);

    return (
      <Table
        data={data}
        hover={true}
        pagination={true}
        search={true}
        options={this.tableOpts}
        exportFile={this._exportFileName("network-uptime")}
        exportFunc={_export}
      >
        <Column
          key="date"
          dataField="date"
          dataSort={true}
          isKey={true}
          dataFormat={this._formatDate.bind(this)}
        >
          Date
        </Column>
        <Column
          key="uptime"
          dataField="uptime"
          dataSort={true}
          dataFormat={this._formatUptime}
        >
          Uptime
        </Column>
      </Table>
    );
  }

  _renderView() {
    const { report, network, networkOptions, start, end, storesLoaded } =
      this.props;

    const { submitted, errorMessage } = this.state;
    const title = network
      ? `Network Uptime: ${network.name}`
      : "Network Uptime";

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    return (
      <div>
        <Notification error={errorMessage} />
        <ReportFilter
          network={network}
          options={networkOptions}
          start={start}
          end={end}
          onRun={this._onReportRun.bind(this)}
          submitted={submitted}
        />
        <Box title={title}>
          <UptimeChart data={report} />
          {this._renderNetworkUptimeReport()}
        </Box>
        <div className="report-help text-muted">
          <span>
            This report shows daily uptime percentage of a selected network. The
            uptime percentage of a network is the average uptime of its
            constituent devices:
          </span>
          <blockquote>
            Daily uptime of a network = (total uptime of devices in the network)
            / (# of devices in the network) x 100
          </blockquote>
          <span>
            To view device-specific uptime percentages:
            <ol>
              <li>Run the network uptime report.</li>
              <li>Click the date value you want to explore.</li>
              <li>
                Click the device id to view historical uptimes for that device.
              </li>
            </ol>
            The uptime percentage for a device is calculated by dividing the
            number of active windows by the total window count:
          </span>
          <blockquote>
            Daily uptime of a device = (# of windows) / (total window count in a
            day) x 100
          </blockquote>
          <span>
            A device is considered <b>online</b> if it has made a connection to
            our system at least once within a 5 minute interval.
          </span>
          <span>
            If a device is moved to a new network, its uptime on that date will
            be calculated from the change time to the end of the day. The
            calculated uptime will only affect its new network. For example if a
            device is moved from sandbox to production in the middle of the day,
            only the rest of the day will be considered when calculating the
            uptime. The uptime report would show:
          </span>
          <span>
            Sandbox network:
            <ul>
              <li>Jan 1st, device-1: 100%</li>
              <li>Jan 2nd, device-1: 50%</li>
            </ul>
          </span>
          <span>
            Production network:
            <ul>
              <li>Jan 2nd, device-1: 50%</li>
              <li>Jan 3rd, device-1: 100%</li>
            </ul>
          </span>
          <span></span>
        </div>
      </div>
    );
  }

  render() {
    const view = this._renderView();

    return <Page title="Uptime Report">{view}</Page>;
  }
}

export { UptimeReportPage };
